import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { careTeamService } from '../../../services/careTeamService';
import api from '../../../config/api';

const ProviderProfile = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);


  // Load provider data
  useEffect(() => {
    const loadProviderData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if provider data was passed from FindProvider page
        const passedProvider = location.state?.provider;
        
        if (passedProvider) {
          // Use the passed provider data
          setProvider(passedProvider);
          setLoading(false);
        } else {
          // Fetch provider data from API using user_id
          const response = await careTeamService.getCareTeamMember(providerId);
          
          if (response.success) {
            setProvider(response.data);
          } else {
            setError(response.error);
          }
        }
      } catch (err) {
        setError({
          message: 'Failed to load provider details. Please try again.',
          status: 500
        });
        console.error('Error loading provider:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProviderData();
  }, [providerId, location.state]);

  // Generate next 7 days for appointment booking
  const getNextWeekDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        date: date.toISOString().split('T')[0],
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString('en-US', { month: 'short' })
      });
    }
    return dates;
  };

  // Load free slots for selected provider and date
  const loadFreeSlots = async (userId, date) => {
    try {
      setLoadingSlots(true);
      
      const dateObj = new Date(date);
      // Convert to Monday=0, Sunday=6 format
      const weekDay = (dateObj.getDay() + 6) % 7; // 0=Monday, 1=Tuesday, ..., 6=Sunday
      
      const response = await api.get(`/planify/event/public/user/${userId}/free-slot`, {
        params: {
          date: date,
          week_day: weekDay
        }
      });

      if (response.data && Array.isArray(response.data)) {
        // Format the slots to show start_time and end_time
        const formattedSlots = response.data.map(slot => {
          const startTime = new Date(slot.start_time);
          const endTime = new Date(slot.end_time);
          
          // Format time to 12-hour format
          const formatTime = (date) => {
            return date.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          };
          
          return {
            time: formatTime(startTime),
            start_time: slot.start_time,
            end_time: slot.end_time,
            availability_id: slot.availability_id,
            available: true,
            display_time: `${formatTime(startTime)} - ${formatTime(endTime)}`,
            start_display: formatTime(startTime),
            end_display: formatTime(endTime)
          };
        });
        
        setAvailableSlots(formattedSlots);
      } else {
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error loading free slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Handle date selection
  const handleDateSelection = (date) => {
    setSelectedDate(date);
    setSelectedTime(''); // Reset selected time when date changes
    setSelectedSlot(null); // Reset selected slot when date changes
    
    // Load free slots for selected provider and date
    if (provider?.user_id) {
      loadFreeSlots(provider.user_id, date);
    }
  };

  // Handle appointment booking
  const handleQuickBook = () => {
    if (selectedDate && selectedTime && selectedSlot) {
      navigate(`/booking/book-appointment?provider=${providerId}&date=${selectedDate}&time=${selectedTime}`, {
        state: { 
          provider: provider,
          selectedSlot: selectedSlot
        }
      });
    } else {
      alert('Please select a date and time for your appointment');
    }
  };

  // Rating stars component
  const RatingStars = ({ rating, size = 'text-base' }) => {
    return (
      <div className={`flex items-center ${size}`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={star <= rating ? 'text-yellow-500' : 'text-gray-300'}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading provider profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Provider</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/booking/find-provider')}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Browse All Providers
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Provider not found
  if (!provider) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Provider Not Found</h2>
          <p className="text-gray-600 mb-6">The provider you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/booking/find-provider')}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Browse All Providers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Cover Image */}
      <div className="relative h-48 md:h-64 bg-gradient-to-r from-blue-500 to-purple-600">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 h-full flex items-end pb-6">
          <div className="flex items-center space-x-4">
            <div className="text-6xl md:text-8xl bg-white rounded-full p-4 shadow-lg">
              👩‍⚕️
            </div>
            <div className="text-white">
              <h1 className="text-2xl md:text-3xl font-bold">{provider.user_name}</h1>
              <p className="text-lg opacity-90">{provider.professional_title}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="bg-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center">
                <RatingStars rating={provider.rating || 0} />
                <span className="ml-2 font-semibold">{provider.rating || 'N/A'}</span>
                <span className="ml-1 text-gray-500">({provider.total_reviews || 0})</span>
              </div>
              <div className="flex items-center text-gray-600">
                <span className="mr-2">⏱</span>
                <span>{provider.experience_years} years experience</span>
              </div>
              {provider.is_public && (
                <div className="flex items-center text-blue-500">
                  <span className="mr-1">✓</span>
                  <span>Verified</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/booking/quick-booking?provider=${providerId}`, {
                  state: { provider: provider }
                })}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                🚀 Quick Book
              </button>
              <button
                onClick={() => navigate(`/booking/book-appointment?provider=${providerId}`, {
                  state: { provider: provider }
                })}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                📅 Book Appointment
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Navigation Tabs */}
            <div className="bg-white rounded-xl shadow-md">
              <div className="border-b">
                <div className="flex overflow-x-auto">
                  {['overview', 'services', 'reviews', 'qualifications', 'faqs'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-6 py-3 capitalize font-medium whitespace-nowrap transition-colors ${
                        activeTab === tab
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold mb-3">About</h3>
                      <div className="text-gray-700 whitespace-pre-line">
                        {provider.about_summary || 'No description available'}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-3">Professional Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Professional Title</h4>
                          <p className="text-gray-600">{provider.professional_title}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Experience</h4>
                          <p className="text-gray-600">{provider.experience_years} years</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Consultation Fee</h4>
                          <p className="text-gray-600">₹{provider.consultation_fee}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Availability</h4>
                          <p className="text-gray-600">{provider.availability_status}</p>
                        </div>
                      </div>
                    </div>

                    {provider.organization_name && (
                    <div>
                        <h3 className="text-xl font-semibold mb-3">Organization</h3>
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <p className="font-medium text-blue-900">{provider.organization_name}</p>
                      </div>
                    </div>
                    )}

                    <div>
                      <h3 className="text-xl font-semibold mb-3">Contact Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Email</h4>
                          <p className="text-gray-600">{provider.user_email}</p>
                            </div> */}
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Mobile</h4>
                          <p className="text-gray-600">{provider.user_mobile}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Services Tab */}
                {activeTab === 'services' && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold mb-4">Services & Fees</h3>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div>
                        <p className="font-medium">Consultation Session</p>
                        <p className="text-sm text-gray-600">Standard consultation</p>
                        </div>
                        <div className="text-right">
                        <p className="font-semibold text-lg">₹{provider.consultation_fee}</p>
                          <button
                          onClick={() => navigate(`/booking/book-appointment?provider=${providerId}`, {
                            state: { provider: provider }
                          })}
                            className="text-sm text-blue-500 hover:text-blue-600"
                          >
                            Book Now →
                          </button>
                        </div>
                      </div>
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700">
                        💡 Insurance coverage may apply. Contact your provider for details.
                      </p>
                    </div>
                  </div>
                )}

                {/* Reviews Tab */}
                {activeTab === 'reviews' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-semibold">Patient Reviews</h3>
                      <button
                        onClick={() => setShowReviewModal(true)}
                        className="text-blue-500 hover:text-blue-600 font-medium"
                      >
                        Write a Review
                      </button>
                    </div>
                    
                    {/* Rating Summary */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <div className="flex items-center justify-between">
                        <div className="text-center">
                          <p className="text-3xl font-bold">{provider.rating || 'N/A'}</p>
                          <RatingStars rating={provider.rating || 0} size="text-lg" />
                          <p className="text-sm text-gray-600 mt-1">
                            {provider.total_reviews || 0} reviews
                          </p>
                        </div>
                        <div className="flex-1 ml-8">
                          <p className="text-sm text-gray-600">No detailed rating breakdown available</p>
                        </div>
                      </div>
                    </div>

                    {/* No Reviews Message */}
                    <div className="text-center py-8">
                      <p className="text-gray-500 mb-4">No reviews available yet</p>
                      <p className="text-sm text-gray-400">Be the first to review this provider</p>
                        </div>
                  </div>
                )}

                {/* Qualifications Tab */}
                {activeTab === 'qualifications' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold mb-4">Professional Information</h3>
                      <div className="space-y-4">
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Professional Title</h4>
                          <p className="text-gray-600">{provider.professional_title}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">Years of Experience</h4>
                          <p className="text-gray-600">{provider.experience_years} years</p>
                            </div>
                        {provider.organization_name && (
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <h4 className="font-medium text-gray-900 mb-2">Organization</h4>
                            <p className="text-gray-600">{provider.organization_name}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-4">Contact Information</h3>
                      <div className="space-y-3">
                        {/* <div className="flex items-start">
                          <span className="text-blue-500 mr-3 mt-1">📧</span>
                          <div>
                            <p className="font-medium">Email</p>
                            <p className="text-sm text-gray-600">{provider.user_email}</p>
                          </div>
                        </div> */}
                        <div className="flex items-start">
                          <span className="text-green-500 mr-3 mt-1">📱</span>
                            <div>
                            <p className="font-medium">Mobile</p>
                            <p className="text-sm text-gray-600">{provider.user_mobile}</p>
                            </div>
                          </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* FAQs Tab */}
                {activeTab === 'faqs' && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold mb-4">Frequently Asked Questions</h3>
                    <div className="space-y-4">
                      <div className="border rounded-lg p-4">
                        <p className="font-medium mb-2">How long is each consultation session?</p>
                        <p className="text-gray-700">Standard consultation sessions are typically 45-60 minutes. The first consultation may take longer for comprehensive assessment.</p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="font-medium mb-2">What is the consultation fee?</p>
                        <p className="text-gray-700">The consultation fee is ₹{provider.consultation_fee} per session. Payment is required at the time of booking.</p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="font-medium mb-2">How can I book an appointment?</p>
                        <p className="text-gray-700">You can book an appointment using the "Book Appointment" button on this page or contact the provider directly.</p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="font-medium mb-2">What is the provider's availability?</p>
                        <p className="text-gray-700">Current availability: {provider.availability_status}</p>
                      </div>
                      </div>
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                      <p className="font-medium mb-2">Have more questions?</p>
                      <p className="text-sm text-gray-700 mb-3">
                        Feel free to reach out during your consultation or contact our support team.
                      </p>
                      <button
                        onClick={() => navigate('/contact')}
                        className="text-blue-500 hover:text-blue-600 font-medium"
                      >
                        Contact Support →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Similar Providers */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold mb-4">Find More Providers</h3>
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">Discover more mental health professionals</p>
              <Link
                to="/booking/find-provider"
                  className="inline-block px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                  Browse All Providers →
              </Link>
              </div>
            </div>
          </div>

          {/* Right Column - Booking & Contact */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Booking Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4">Book Appointment</h3>
              
              {/* Consultation Fee */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Consultation Fee</p>
                <p className="text-2xl font-bold text-gray-900">₹{provider.consultation_fee}</p>
                <p className="text-xs text-gray-500">per session</p>
              </div>

              {/* Date Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Date
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {getNextWeekDates().map((dateObj) => (
                    <button
                      key={dateObj.date}
                      onClick={() => handleDateSelection(dateObj.date)}
                      className={`p-2 text-center rounded-lg border transition-colors ${
                        selectedDate === dateObj.date
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      <p className="text-xs">{dateObj.day}</p>
                      <p className="font-semibold">{dateObj.dayNum}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Slot Selection */}
              {selectedDate && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Slots
                  </label>
                  {loadingSlots ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                      <p className="text-gray-600 mt-2 text-sm">Loading available slots...</p>
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                      {availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedTime(slot.time);
                            setSelectedSlot(slot);
                          }}
                          className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                            selectedTime === slot.time
                              ? 'bg-blue-500 text-white border-blue-500'
                              : 'bg-white hover:bg-gray-50 border-gray-300'
                          }`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-600">
                      <p className="text-sm">No available slots for this date.</p>
                      <p className="text-xs mt-1">Please select a different date.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Book Button */}
              <button
                onClick={handleQuickBook}
                className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                Book Appointment
              </button>

              {/* Alternative Actions */}
              <div className="mt-4 space-y-2">
                <button
                  onClick={() => navigate(`/booking/quick-booking?provider=${providerId}`)}
                  className="w-full py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
                >
                  🚀 Quick Booking
                </button>
                <button className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                  💬 Message Provider
                </button>
              </div>

              {/* Response Time */}
              <p className="text-center text-sm text-gray-500 mt-4">
                Contact provider for response time
              </p>
            </div>

            {/* Contact & Location Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
              
              <div className="space-y-3">
                {provider.organization_name && (
                <div className="flex items-start">
                    <span className="text-gray-400 mr-3 mt-1">🏢</span>
                  <div>
                      <p className="font-medium">{provider.organization_name}</p>
                      <p className="text-sm text-gray-600">Organization</p>
                  </div>
                </div>
                )}

                {/* <div className="flex items-center">
                  <span className="text-gray-400 mr-3">📧</span>
                  <a
                    href={`mailto:${provider.user_email}`}
                    className="text-sm text-gray-600 hover:text-blue-500"
                  >
                    {provider.user_email}
                  </a>
                </div> */}

                <div className="flex items-center">
                  <span className="text-gray-400 mr-3">📱</span>
                  <a
                    href={`tel:${provider.user_mobile}`}
                    className="text-sm text-gray-600 hover:text-blue-500"
                  >
                    {provider.user_mobile}
                  </a>
                </div>

                <div className="flex items-center">
                  <span className="text-gray-400 mr-3">📅</span>
                  <span className="text-sm text-gray-600">{provider.availability_status}</span>
                </div>
              </div>

              {/* Share Profile */}
              <div className="mt-6 pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-2">Share Profile</p>
                <div className="flex gap-2">
                  <button className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm">
                    📋 Copy Link
                  </button>
                  <button className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm">
                    📤 Share
                  </button>
                </div>
              </div>
            </div>

            {/* Emergency Support */}
            <div className="bg-red-50 rounded-xl p-6">
              <h4 className="font-semibold text-red-800 mb-2">Need Immediate Help?</h4>
              <p className="text-sm text-red-700 mb-3">
                If you're experiencing a mental health crisis, please reach out immediately.
              </p>
              <Link
                to="/emergency"
                className="block w-full text-center py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
              >
                🆘 Emergency Support
              </Link>
              <p className="text-center text-sm text-red-700 mt-2">
                24/7 Helpline: +1 (888) 404-7470
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-lg w-full p-6"
          >
            <h3 className="text-xl font-semibold mb-4">Write a Review</h3>
            <form>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Rating
                </label>
                <div className="flex gap-2 text-3xl">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="text-gray-300 hover:text-yellow-500 transition-colors"
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="4"
                  placeholder="Share your experience..."
                ></textarea>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowReviewModal(false)}
                  className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Submit Review
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ProviderProfile;
